/**
 * StepInjectPanel — P10 interactive answer-injection for the live scrubber.
 *
 * Beyond the if/try path gate (which forces an exclusive arm pick), a process's
 * trace has more nondeterminism the scrubber can pin so "Save as test" captures a
 * fully-constrained session: which `select` cases ran, a failure injected on a
 * step, and a step's delay (`after`/`takes` — the timing that also decides a
 * race). This panel surfaces those as opt-in controls for the ACTIVE step,
 * writing each into the walker overlay via the playback mutators. The producer
 * consumes the resulting `CaptureSession` on save — the panel never re-projects
 * the trace itself (walkProcess still renders the structure off `branchSelection`);
 * the injected answers show here as the accumulated session.
 */
import { useCallback, type ReactElement } from "react";
import type { WalkResult, FailureAnswer, DelayAnswer } from "@archlang/engine";
import type { WalkerState } from "../hooks/useProcessPlayback.js";
import { injectTargetsFor } from "./stepInject.js";

export interface StepInjectPanelProps {
  readonly walkResult: WalkResult | null;
  readonly stepIndex: number | null;
  readonly state: WalkerState;
  readonly setSelectRuns: (nodeId: string, indices: readonly number[] | null) => void;
  readonly setFailure: (nodeId: string, answer: FailureAnswer | null) => void;
  readonly setDelay: (nodeId: string, answer: DelayAnswer | null) => void;
}

const FIELD =
  "w-full rounded-sm border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[11px] text-slate-200 placeholder:text-slate-500";
const LABEL = "text-[11px] font-medium text-slate-300";

export function StepInjectPanel({
  walkResult,
  stepIndex,
  state,
  setSelectRuns,
  setFailure,
  setDelay,
}: StepInjectPanelProps): ReactElement {
  const target = injectTargetsFor(walkResult, stepIndex);

  const failure = target ? state.failures?.get(target.nodeId) : undefined;
  const delay = target ? state.delays?.get(target.nodeId) : undefined;
  const selectFrame = target?.selectFrame;
  // Unpinned default: the first `runCount` cases (bare `select` runs all) — so a
  // `select one` shows one case checked, not every case (matching the legend and
  // what the producer would run). The user is still prompted to confirm; an
  // unanswered select stays unpinned and the capture warns (under-constrained).
  const runs = selectFrame
    ? state.selectRuns?.get(selectFrame.nodeId) ??
      selectFrame.cases.map((_, i) => i).slice(0, selectFrame.runCount ?? selectFrame.cases.length)
    : undefined;

  const toggleFailure = useCallback(
    (on: boolean) => {
      if (!target) return;
      setFailure(target.nodeId, on ? { ref: target.to, outcomes: [{ kind: "fail" }] } : null);
    },
    [target, setFailure],
  );

  const setCause = useCallback(
    (cause: string) => {
      if (!target) return;
      const c = cause.trim();
      setFailure(target.nodeId, { ref: target.to, outcomes: [c ? { kind: "fail", cause: c } : { kind: "fail" }] });
    },
    [target, setFailure],
  );

  const editDelay = useCallback(
    (axis: "after" | "takes", duration: string) => {
      if (!target) return;
      const d = duration.trim();
      setDelay(target.nodeId, d ? { ref: target.to, axis, duration: d } : null);
    },
    [target, setDelay],
  );

  const toggleCase = useCallback(
    (index: number) => {
      if (!selectFrame || !runs) return;
      const next = runs.includes(index) ? runs.filter((i) => i !== index) : [...runs, index].sort((a, b) => a - b);
      setSelectRuns(selectFrame.nodeId, next);
    },
    [selectFrame, runs, setSelectRuns],
  );

  if (!target) {
    return (
      <p className="text-[11px] leading-snug text-slate-500">
        Step through the process to inject a failure, delay, or select run-set at each step.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid="step-inject-panel">
      <div className="truncate text-[11px] text-slate-400" title={target.to.join(".")}>
        <span className="text-slate-500">at </span>
        <span className="font-mono text-slate-300">{target.to.join(".") || "—"}</span>
      </div>

      {selectFrame ? (
        <fieldset className="flex flex-col gap-1">
          <legend className={LABEL}>
            select{selectFrame.head ? ` "${selectFrame.head}"` : ""}
            {selectFrame.runCount !== undefined ? ` · ${selectFrame.runCount} of ${selectFrame.cases.length} run` : ""}
          </legend>
          {selectFrame.cases.map((label, i) => (
            <label key={label} className="flex items-center gap-1.5 text-[11px] text-slate-300">
              <input
                type="checkbox"
                checked={runs?.includes(i) ?? false}
                onChange={() => toggleCase(i)}
                data-testid={`select-case-${label}`}
              />
              {label}
            </label>
          ))}
        </fieldset>
      ) : null}

      {target.failable ? (
        <>
          <label className="flex items-center gap-1.5 text-[11px] text-slate-300">
            <input
              type="checkbox"
              checked={failure !== undefined}
              onChange={(e) => toggleFailure(e.target.checked)}
              data-testid="inject-failure"
            />
            inject failure
          </label>
          {failure ? (
            <input
              type="text"
              value={failure.outcomes[0]?.kind === "fail" ? failure.outcomes[0].cause ?? "" : ""}
              onChange={(e) => setCause(e.target.value)}
              placeholder="cause (optional)"
              className={FIELD}
              data-testid="failure-cause"
            />
          ) : null}

          <fieldset className="flex flex-col gap-1">
            <legend className={LABEL}>delay</legend>
            <div className="flex items-center gap-1">
              <select
                value={delay?.axis ?? "after"}
                onChange={(e) => editDelay(e.target.value as "after" | "takes", delay?.duration ?? "")}
                className={FIELD + " w-auto"}
                data-testid="delay-axis"
              >
                <option value="after">after</option>
                <option value="takes">takes</option>
              </select>
              <input
                type="text"
                value={delay?.duration ?? ""}
                onChange={(e) => editDelay(delay?.axis ?? "after", e.target.value)}
                placeholder="e.g. 2h"
                className={FIELD}
                data-testid="delay-duration"
              />
            </div>
          </fieldset>
        </>
      ) : null}
    </div>
  );
}
